import CheckRounded from '@mui/icons-material/CheckRounded';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { Reveal } from '@/components/atoms';
import { EnquiryFormShell, HoneypotField, SelectField, TextInputField } from '@/components/forms';
import { NextStepList } from '@/components/molecules';
import { Section } from '@/components/organisms';
import { fleetSizes } from '@/schemas/enquiry';

import {
  fleetAssurances,
  fleetEyebrow,
  fleetLede,
  fleetSectorOptions,
  fleetSteps,
  fleetSubmitLabel,
  fleetTitle,
  FLEET_FORM_ID,
  FLEET_TITLE_ID,
  WHATSAPP_MESSAGE,
} from '../content';
import { type FleetEnquiryFormState } from '../hooks/useFleetEnquiryForm';

type Props = { form: FleetEnquiryFormState };

/**
 * The page's conversion point: the organisation enquiry, taken here rather
 * than on another route.
 *
 * ## Why the form is on this page at all
 *
 * /clients was a page of proof with its only ask three bands below the proof,
 * and that ask was a link to `/quote` — a form written for someone buying one
 * car for themselves, which asks "car you want" and "your budget" and has
 * nowhere to put the organisation's name or the number of vehicles. An
 * institutional reader convinced by the roster was being sent to the wrong
 * form, one navigation away, and every navigation between conviction and
 * typing is a place to lose them. So the right form sits directly under the
 * roster, and the roster's own call to action scrolls to it.
 *
 * ## The left column is the reason to fill it in
 *
 * A form on its own is a cost with no stated benefit. The left column states
 * the benefit twice, in the two ways a procurement reader weighs it: what the
 * request costs them (nothing, and nothing is reserved) and what the button
 * actually starts (`fleetSteps`). Both are facts about this form, not claims
 * about the company — see `../content` for why that line is held.
 *
 * ## The anchor
 *
 * The band carries `id="fleet-quote"`, which three links point at: the opening
 * band's button, the sector call to action in the roster, and the home page's
 * client wall. `scrollMarginTop` keeps the heading clear of the sticky header
 * when any of them lands.
 */
export function FleetQuoteBand({ form }: Props) {
  const { control, register, onSubmit, isPending, error, receipt } = form;

  return (
    <Section
      id={FLEET_FORM_ID}
      tone="default"
      pattern="beams"
      labelledBy={FLEET_TITLE_ID}
      sx={{ scrollMarginTop: (theme) => theme.spacing(20) }}
    >
      <Grid container spacing={{ xs: 8, md: 12 }} alignItems="flex-start">
        <Grid size={{ xs: 12, md: 5 }}>
          <Reveal direction="left">
            <Stack spacing={4}>
              <Typography variant="overline" sx={{ color: 'primary.main' }}>
                {fleetEyebrow}
              </Typography>
              <Typography id={FLEET_TITLE_ID} variant="h2" component="h2">
                {fleetTitle}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {fleetLede}
              </Typography>

              <Stack component="ul" spacing={2} sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {fleetAssurances.map((line) => (
                  <Stack component="li" key={line} direction="row" spacing={3}>
                    <CheckRounded aria-hidden sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="body2" color="text.secondary">
                      {line}
                    </Typography>
                  </Stack>
                ))}
              </Stack>

              {/* What the submit button starts, which is the question that
                  stops an institutional sender more often than price does. */}
              <Stack spacing={4} sx={{ pt: 2 }}>
                <Typography component="h3" variant="h6">
                  What happens next
                </Typography>
                <NextStepList steps={fleetSteps} />
              </Stack>
            </Stack>
          </Reveal>
        </Grid>

        <Grid size={{ xs: 12, md: 7 }}>
          <Reveal direction="right">
            <EnquiryFormShell
              title="Request a quotation"
              description="Five fields. The specification we can settle on the phone."
              submitLabel={fleetSubmitLabel}
              onSubmit={onSubmit}
              isPending={isPending}
              error={error}
              receipt={receipt}
              whatsappMessage={WHATSAPP_MESSAGE}
            >
              <HoneypotField field={register('companyWebsite')} />
              <Grid container spacing={4}>
                {/* The organisation first. It is the field that makes this a
                    different form from the quotation one, and asking it first
                    tells the sender in one glance that they are on the right
                    one. */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextInputField
                    control={control}
                    name="organisation"
                    label="Organisation"
                    placeholder="e.g. Ministry of Works, Stanbic Bank"
                    autoComplete="organization"
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <SelectField
                    control={control}
                    name="sector"
                    label="Sector"
                    options={fleetSectorOptions}
                    emptyLabel="Choose your sector"
                    help="Only so we can put your request in front of whoever handles organisations like yours. Leave it unset if none of them fit."
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextInputField
                    control={control}
                    name="vehiclesNeeded"
                    label="Vehicles you need"
                    placeholder="e.g. Toyota Hilux double cabin, 2019 or newer"
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <SelectField
                    control={control}
                    name="fleetSize"
                    label="How many"
                    options={fleetSizes}
                    emptyLabel="Not decided yet"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextInputField
                    control={control}
                    name="fullName"
                    label="Your name"
                    autoComplete="name"
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextInputField
                    control={control}
                    name="phone"
                    label="WhatsApp number"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="+256 7XX XXX XXX"
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextInputField
                    control={control}
                    name="email"
                    label="Work email"
                    type="email"
                    autoComplete="email"
                    helperText="Where the written quotation is sent."
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextInputField
                    control={control}
                    name="message"
                    label="Anything else we should know"
                    placeholder="Delivery location, timing, specification, procurement requirements."
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>
            </EnquiryFormShell>
          </Reveal>
        </Grid>
      </Grid>
    </Section>
  );
}
